import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getItemCategories,
    autoCompleteItem,
    getViewItemCodeUpdation,
    getHsnUpd,
    getAllUnitsUpd,
    itemCodeCheckPendings,
    saveItemCodeUpdateDetails,
} from '../../api/PurchaseAPI/itemCodeUpdationAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

export const fetchItemCategoryList = createAsyncThunk(
    'itemCodeUpdation/fetchItemCategoryList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getItemCategories();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load item categories');
        }
    }
);

/** Prefix-search suggestions for the item search box */
export const fetchItemSuggestions = createAsyncThunk(
    'itemCodeUpdation/fetchItemSuggestions',
    async ({ prefix, type, category }, { rejectWithValue }) => {
        try {
            const res = await autoCompleteItem(prefix, type, category);
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load suggestions');
        }
    }
);

/** Loads the item's current details + selectable HSN list + unit list together (mirrors the
 *  legacy ViewItemCodeUpdation action, which populated all three from one server round trip). */
export const fetchItemForUpdate = createAsyncThunk(
    'itemCodeUpdation/fetchItemForUpdate',
    async (itemCode, { rejectWithValue }) => {
        try {
            const [detailsRes, hsnRes, unitsRes] = await Promise.all([
                getViewItemCodeUpdation(itemCode),
                getHsnUpd(itemCode),
                getAllUnitsUpd(),
            ]);
            return {
                details: detailsRes.data?.Data || null,
                hsnList: hsnRes.data?.Data || [],
                unitsList: unitsRes.data?.Data || [],
            };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load item details');
        }
    }
);

/** Blocks submission if the item already has a pending update awaiting approval */
export const checkItemCodePendings = createAsyncThunk(
    'itemCodeUpdation/checkItemCodePendings',
    async (itemCode, { rejectWithValue }) => {
        try {
            const res = await itemCodeCheckPendings(itemCode);
            return res.data?.Data;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to check pending updates');
        }
    }
);

export const submitItemCodeUpdate = createAsyncThunk(
    'itemCodeUpdation/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await saveItemCodeUpdateDetails(payload);
            const status = res.data?.Data;
            if (status !== 'Successfull') {
                return rejectWithValue(status || 'Failed to save item code update');
            }
            return status;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save item code update');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    categoryList: [],
    suggestions: [],
    details: null,
    hsnList: [],
    unitsList: [],
    saveResult: null,
    loading: {
        categoryList: false,
        suggestions: false,
        itemForUpdate: false,
        checkPendings: false,
        save: false,
    },
    errors: {
        categoryList: null,
        suggestions: null,
        itemForUpdate: null,
        checkPendings: null,
        save: null,
    },
};

const itemCodeUpdationSlice = createSlice({
    name: 'itemCodeUpdation',
    initialState,
    reducers: {
        clearItemSuggestions: (s) => { s.suggestions = []; s.errors.suggestions = null; },
        clearItemForUpdate:   (s) => { s.details = null; s.hsnList = []; s.unitsList = []; s.errors.itemForUpdate = null; },
        clearSaveResult:      (s) => { s.saveResult = null; s.errors.save = null; },
        resetItemCodeUpdation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchItemCategoryList.pending,   (s) => { s.loading.categoryList = true; s.errors.categoryList = null; })
            .addCase(fetchItemCategoryList.fulfilled, (s, a) => { s.loading.categoryList = false; s.categoryList = a.payload; })
            .addCase(fetchItemCategoryList.rejected,  (s, a) => { s.loading.categoryList = false; s.errors.categoryList = a.payload; });

        builder
            .addCase(fetchItemSuggestions.pending,   (s) => { s.loading.suggestions = true; s.errors.suggestions = null; })
            .addCase(fetchItemSuggestions.fulfilled, (s, a) => { s.loading.suggestions = false; s.suggestions = a.payload; })
            .addCase(fetchItemSuggestions.rejected,  (s, a) => { s.loading.suggestions = false; s.errors.suggestions = a.payload; s.suggestions = []; });

        builder
            .addCase(fetchItemForUpdate.pending, (s) => {
                s.loading.itemForUpdate = true; s.errors.itemForUpdate = null;
                s.details = null; s.hsnList = []; s.unitsList = [];
            })
            .addCase(fetchItemForUpdate.fulfilled, (s, a) => {
                s.loading.itemForUpdate = false;
                s.details = a.payload.details;
                s.hsnList = a.payload.hsnList;
                s.unitsList = a.payload.unitsList;
            })
            .addCase(fetchItemForUpdate.rejected, (s, a) => { s.loading.itemForUpdate = false; s.errors.itemForUpdate = a.payload; });

        builder
            .addCase(checkItemCodePendings.pending,   (s) => { s.loading.checkPendings = true; s.errors.checkPendings = null; })
            .addCase(checkItemCodePendings.fulfilled, (s) => { s.loading.checkPendings = false; })
            .addCase(checkItemCodePendings.rejected,  (s, a) => { s.loading.checkPendings = false; s.errors.checkPendings = a.payload; });

        builder
            .addCase(submitItemCodeUpdate.pending,   (s) => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitItemCodeUpdate.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitItemCodeUpdate.rejected,  (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearItemSuggestions,
    clearItemForUpdate,
    clearSaveResult,
    resetItemCodeUpdation,
} = itemCodeUpdationSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectItemCategoryList = (s) => s.itemCodeUpdation.categoryList;
export const selectItemSuggestions  = (s) => s.itemCodeUpdation.suggestions;
export const selectItemDetails      = (s) => s.itemCodeUpdation.details;
export const selectItemHsnList      = (s) => s.itemCodeUpdation.hsnList;
export const selectItemUnitsList    = (s) => s.itemCodeUpdation.unitsList;
export const selectItemSaveResult   = (s) => s.itemCodeUpdation.saveResult;

export const selectItemCategoryListLoading = (s) => s.itemCodeUpdation.loading.categoryList;
export const selectItemSuggestionsLoading  = (s) => s.itemCodeUpdation.loading.suggestions;
export const selectItemForUpdateLoading    = (s) => s.itemCodeUpdation.loading.itemForUpdate;
export const selectCheckPendingsLoading    = (s) => s.itemCodeUpdation.loading.checkPendings;
export const selectItemSaveLoading         = (s) => s.itemCodeUpdation.loading.save;
export const selectItemSaveError           = (s) => s.itemCodeUpdation.errors.save;

export default itemCodeUpdationSlice.reducer;
